import React from "react";
import { Input } from "@shared/ui/Input";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "next-i18next";
import { HorizontalFormField } from "@shared/ui/HorizontalFormField";
import { Select } from "@shared/ui/Select";
import { IApplicationForm } from "@entities/Product/Application/types";
import { extractNumbers, setSpacesInNumber } from "@shared/lib";
import { useUnit } from "effector-react";
import {
  $collateralCarModels,
  $collateralCarTypes,
  getCarModelsCollateralFx,
  getCarTypesCollateralFx,
} from "@entities/Product/Application/model";
import { findOption } from "@entities/Product/Application/utils";
import { ISelectOption } from "@shared/types";

interface IProps {
  brandOptions: { value: string; label: string }[];
}

export function CollateralCarFields({ brandOptions }: IProps) {
  const { t } = useTranslation();
  const methods = useFormContext<IApplicationForm>();
  const [typeOptions, modelOptions, getCarTypes, getCarModels] = useUnit([
    $collateralCarTypes,
    $collateralCarModels,
    getCarTypesCollateralFx,
    getCarModelsCollateralFx,
  ]);

  const [isTypesLoading, isModelLoading] = useUnit([
    getCarTypesCollateralFx.pending,
    getCarModelsCollateralFx.pending,
  ]);

  const { collateral_car_brand } = methods.watch();

  return (
    <>
      <HorizontalFormField label={t("Марка")}>
        <Controller
          control={methods.control}
          name="collateral_car_brand"
          render={({ field, fieldState }) => (
            <Select
              value={findOption(brandOptions, field.value)}
              onChange={(newValue: ISelectOption) => {
                field.onChange(newValue.value);
                getCarTypes(newValue.value);
                methods.setValue("collateral_car_type", "");
                methods.setValue("collateral_car_name", "");
              }}
              hasError={Boolean(fieldState.error)}
              options={brandOptions}
              placeholder={t(`Выберите марку авто`)}
              noOptionsMessage={() => t(`Не найдено`)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Тип")}>
        <Controller
          control={methods.control}
          name="collateral_car_type"
          render={({ field, fieldState }) => (
            <Select
              value={findOption(typeOptions, field.value)}
              onChange={(newValue: ISelectOption) => {
                field.onChange(newValue.value);
                getCarModels({
                  brandCode: collateral_car_brand,
                  typeCode: newValue.value,
                });
                methods.setValue("collateral_car_name", "");
              }}
              hasError={Boolean(fieldState.error)}
              isLoading={isTypesLoading}
              options={typeOptions}
              placeholder={t(`Выберите тип авто`)}
              isDisabled={
                !methods.watch("collateral_car_brand") || isTypesLoading
              }
              noOptionsMessage={() => t(`Не найдено`)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Модель")}>
        <Controller
          control={methods.control}
          name="collateral_car_name"
          render={({ field, fieldState }) => (
            <Select
              value={findOption(modelOptions, field.value)}
              onChange={(newValue: ISelectOption) =>
                field.onChange(newValue.value)
              }
              hasError={Boolean(fieldState.error)}
              options={modelOptions}
              isLoading={isModelLoading}
              placeholder={t(`Выберите модель авто`)}
              isDisabled={
                !methods.watch("collateral_car_type") || isModelLoading
              }
              noOptionsMessage={() => t(`Не найдено`)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Год производства")}>
        <Controller
          name="collateral_car_year"
          control={methods.control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              onChange={(e) => field.onChange(extractNumbers(e.target.value))}
              hasError={Boolean(fieldState.error?.message)}
            />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Номер тех. паспорта")}>
        <Controller
          name="collateral_car_doc_number"
          control={methods.control}
          render={({ field, fieldState }) => (
            <Input {...field} hasError={Boolean(fieldState.error?.message)} />
          )}
        />
      </HorizontalFormField>
      <HorizontalFormField label={t("Цена авто")}>
        <Controller
          name="collateral_car_price"
          control={methods.control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              onChange={(e) =>
                field.onChange(
                  setSpacesInNumber(extractNumbers(e.target.value))
                )
              }
              inputMode="numeric"
              hasError={Boolean(fieldState.error?.message)}
            />
          )}
        />
      </HorizontalFormField>
    </>
  );
}
